// Time zone of the user, resolved from their public IP location (not from device settings).
// All meeting dates are stored in UTC; these helpers convert them to/from this zone.
// The zone is a MobX observable, so observer components re-render when the location changes.
import {observable, runInAction} from "mobx"

const LOOKUP_TIMEOUT_MS = 3000
const RECHECK_INTERVAL_MS = 10 * 60 * 1000

const PROVIDERS = [
    {url: "https://ipapi.co/json/", pick: (data) => data.timezone},
    {url: "https://ipwho.is/", pick: (data) => data.timezone && data.timezone.id},
]

const zoneBox = observable.box(Intl.DateTimeFormat().resolvedOptions().timeZone)

const isValidTimeZone = (zone) => {
    try {
        new Intl.DateTimeFormat("en-US", {timeZone: zone})
        return true
    } catch (e) {
        return false
    }
}

const fetchWithTimeout = async (url) => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS)
    try {
        const response = await fetch(url, {signal: controller.signal, cache: "no-store"})
        return await response.json()
    } finally {
        clearTimeout(timer)
    }
}

export const initTimeZone = async () => {
    for (const provider of PROVIDERS) {
        try {
            const zone = provider.pick(await fetchWithTimeout(provider.url))
            if (zone && isValidTimeZone(zone)) {
                if (zone !== zoneBox.get()) runInAction(() => zoneBox.set(zone))
                return zone
            }
        } catch (e) {}
    }
    return zoneBox.get()
}

// Re-resolves the location when the tab becomes visible again and periodically,
// so turning a VPN on/off or travelling is picked up without reloading the page.
export const watchTimeZone = () => {
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") initTimeZone()
    })
    setInterval(initTimeZone, RECHECK_INTERVAL_MS)
}

export const getTimeZone = () => zoneBox.get()

const pad = (n) => (n < 10 ? "0" + n : "" + n)

// Calendar parts of a date as seen in the user's time zone (month is 0-based, like Date)
export const getZonedParts = (date) => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: getTimeZone(), hourCycle: "h23",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(new Date(date))
    const get = (type) => Number(parts.find((p) => p.type === type).value)
    return {
        year: get("year"), month: get("month") - 1, day: get("day"),
        hour: get("hour") % 24, minute: get("minute"), second: get("second"),
    }
}

const getOffsetMs = (timestamp) => {
    const p = getZonedParts(timestamp)
    const seconds = timestamp - (timestamp % 1000)
    return Date.UTC(p.year, p.month, p.day, p.hour, p.minute, p.second) - seconds
}

// Wall-clock time in the user's time zone -> Date (UTC instant)
export const zonedTimeToDate = (year, month, day, hour, minute) => {
    const asUtc = Date.UTC(year, month, day, hour, minute)
    let timestamp = asUtc - getOffsetMs(asUtc)
    timestamp = asUtc - getOffsetMs(timestamp)
    return new Date(timestamp)
}

// Local-midnight Date with the same calendar day as `date` in the user's zone (for react-calendar)
export const toCalendarDate = (date) => {
    const p = getZonedParts(date)
    return new Date(p.year, p.month, p.day)
}

export const formatHourMinute = (date) => {
    const p = getZonedParts(date)
    return pad(p.hour) + ":" + pad(p.minute)
}

// Same pieces as new Date(date).toString().split(" "): [weekday, month, day, year, "HH:mm:ss"]
export const getDateStringParts = (date) => {
    const p = getZonedParts(date)
    const names = new Intl.DateTimeFormat("en-US", {timeZone: getTimeZone(), weekday: "short", month: "short"}).formatToParts(new Date(date))
    const name = (type) => names.find((n) => n.type === type).value
    return [name("weekday"), name("month"), pad(p.day), "" + p.year, `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`]
}

export const formatDate = (date, options, locale = "en-GB") =>
    new Date(date).toLocaleDateString(locale, {...options, timeZone: getTimeZone()})
